import React, { useState, useMemo, useEffect } from 'react';
import {
  IconButton,
  Typography,
  Divider,
  TextField,
  Grid,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  Checkbox,
  CircularProgress
} from '@material-ui/core';
import { useNotify } from 'react-admin';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CloseIcon from '@material-ui/icons/Close';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import DesktopMacIcon from '@material-ui/icons/DesktopMac';
import LinkIcon from '@material-ui/icons/Link';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import ImageKitFileUpload from '../../../components/ImageKitFileUpload';
import useAgencyBranchesForAutoComplete from '../../../utils/hooks/useAgencyBranchesForAutoComplete';
import RemoveDialog from './RemoveDialog';
import { validateLink } from '../api';
import { getUIDomain } from '../../../utils/utils';
import CopyLinkInput from './CopyLinkInput';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" color="primary" />;

const BannerDrawer = ({
  onClose = () => {},
  onSave = () => {},
  data = {},
  initialBranches = [],
  uid,
  title = 'באנר' // todo: get the default branches as a prop
}) => {
  const notify = useNotify();
  const [isLinkChecked, setIsLinkChecked] = useState(
    data.isLink !== null && data.isLink !== undefined ? data.isLink : true
  );

  const [branchesInputValue, setBranchesInputValue] = useState('');
  const { agencyBranches } = useAgencyBranchesForAutoComplete();
  const [desktopImage, setDesktopImage] = useState(
    data.desktopImageUrl || null
  );
  const [mobileImage, setMobileImage] = useState(data.mobileImageUrl || null);
  const [linkError, setLinkError] = useState(false);
  const [startDate, setStartDate] = useState(data.startDate || '');
  const [endDate, setEndDate] = useState(data.endDate || '');
  const [fullSearchUrl, setFullSearchUrl] = useState(null);
  const [status, setStatus] = useState(data.status || null);
  const [campaignName, setCampaignName] = useState(data.campaignName || null);
  const [url, setUrl] = useState(data.url || null);
  const [timer, setTimer] = useState(data.timer || false);
  const [selectedBranches, setSelectedBranches] = useState(data.branches || []);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isStatusDisabled, setIsStatusDisabled] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [carsFound, setCarsFound] = useState(null);
  const modified = { ...data };

  useEffect(() => {
    if (data.isLink === null || data.isLink === undefined) {
      setUrl('used-cars');
    }
  }, []);

  useEffect(() => {
    if (isLinkChecked && url && !url.startsWith('used-cars')) {
      setUrl('used-cars');
    }
  }, [isLinkChecked]);

  const createPreviewLink = () => {
    if (!url || !url.startsWith('used-cars')) {
      return '';
    }

    setFullSearchUrl(null);

    const baseUrl = `${getUIDomain()}${url}`;
    let branchesQuery = selectedBranches
      .filter((branch) => branch !== 0)
      .map((branch, i) => `agenciesIds[${i}]=${branch}`)
      .join('&');

    if (branchesQuery === 'agenciesIds[0]=0') branchesQuery = ''; // if all branches are selected, don't include the query

    const separator = baseUrl.includes('?') ? '&' : '?';
    const fullUrl = branchesQuery
      ? `${baseUrl}${separator}${branchesQuery}`
      : baseUrl;

    setFullSearchUrl(fullUrl);
  };

  const validateSearchUrl = async () => {
    let branches = selectedBranches || [];

    if (
      selectedBranches &&
      selectedBranches.length === 1 &&
      selectedBranches[0] === 0
    )
      branches = [];

    try {
      setCarsFound(-1);
      setLoading(true);

      const res = await validateLink({ url, branches });

      setLoading(false);
      setCarsFound(res.count);
    } catch (err) {
      notify('קרתה שגיאה בבדיקת כמות הרכבים בסינון', 'error');
      setLoading(false);
      setCarsFound(-1);
    }
  };

  const areRequiredFieldsFilled = () => {
    return (
      !!startDate &&
      !!endDate &&
      !!campaignName &&
      !!desktopImage &&
      !!mobileImage &&
      (!isLinkChecked ||
        (isLinkChecked && !!url && url.startsWith('used-cars')))
    );
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    if (!startDate || !endDate || startDate > today || today > endDate) {
      setStatus('inactive');
      setIsStatusDisabled(true);
    } else {
      setIsStatusDisabled(false);
    }
  }, [startDate, endDate]);

  const handleSave = (clear = false) => {
    if (clear) {
      Object.keys(modified).forEach((key) => {
        modified[key] = null;
      });

      modified.creatingUid = uid;
    } else {
      modified.startDate = startDate;
      modified.endDate = endDate;
      modified.creatingUid = uid;
      modified.desktopImageUrl = desktopImage;
      modified.mobileImageUrl = mobileImage;
      modified.branches = selectedBranches;
      modified.status = status;
      modified.url = url;
      modified.timer = timer;
      modified.campaignName = campaignName;
      modified.isLink = isLinkChecked;
    }

    modified.location = 'HOME_USEDCARS';
    modified.sourceTable = 'Banner';

    onSave(modified);
  };

  const handleStartDateChange = (val) => {
    setStartDate(val);
    setDirty(true);
  };

  const handleEndDateChange = (val) => {
    setEndDate(val);
    setDirty(true);
  };

  const helpText = `ניתן להעלות קבצים בפורמטים: JPG, PNG, GIF. גודל קובץ מקסימלי 10mb`;

  const hasSomeData = () => {
    // Check if any value in the data object, excluding some keys that always have a value
    return Object.entries(data).some(([key, value]) => {
      if (key === 'id' || key === 'nodeId' || key === 'location') {
        return false;
      }

      return !!value; // Convert value to boolean and check if it's truthy
    });
  };

  const branchOpts = useMemo(() => {
    return [{ id: 0, name: 'בחר הכל' }, ...(agencyBranches || [])];
  }, [agencyBranches]);

  return (
    <div style={{ width: 700, padding: 16 }}>
      {/* Close button */}
      <IconButton
        style={{ position: 'absolute', top: 8, left: 8 }}
        onClick={() => onClose(dirty)}
      >
        <CloseIcon />
      </IconButton>
      {/* Title */}
      <Typography
        variant="h6"
        style={{ marginTop: 4, marginBottom: 8, color: '#519FAB' }}
      >
        {title}
      </Typography>
      {/* Divider */}
      <Divider style={{ marginBottom: 16 }} />
      {/* Switch input */}

      <Grid
        container
        spacing={2}
        style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}
      >
        {/* Switch input */}
        <Grid item style={{ flex: 1 }}>
          <Typography
            variant="body1"
            style={{ marginLeft: 16, fontWeight: 'bold' }}
          >
            סטטוס
          </Typography>
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Switch
                defaultChecked={data.status === 'active'}
                checked={status === 'active'}
                name="status"
                color="primary"
                disabled={isStatusDisabled}
                onChange={(ev) => {
                  setStatus(ev.currentTarget.checked ? 'active' : 'inactive');
                  setDirty(true);
                }}
              />
            }
            label="פעיל"
          />
        </Grid>
        {/* Status label */}
      </Grid>
      {/* Date fields */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={6}>
          <TextField
            required
            helperText={errors.startDate && 'שדה זה חובה'}
            variant="outlined"
            label="תאריך התחלה"
            type="date"
            error={errors.startDate}
            defaultValue={data.startDate || ''}
            onChange={(ev) => {
              handleStartDateChange(ev.currentTarget.value);
            }}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
            InputProps={{
              inputProps: { min: new Date().toISOString().split('T')[0] }
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            required
            error={errors.endDate}
            helperText={errors.endDate && 'שדה זה חובה'}
            variant="outlined"
            label="תאריך סיום"
            type="date"
            defaultValue={data.endDate || ''}
            onChange={(ev) => {
              handleEndDateChange(ev.currentTarget.value);
            }}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
            InputProps={{
              inputProps: {
                min: startDate || new Date().toISOString().split('T')[0]
              }
            }}
          />
        </Grid>
      </Grid>
      {/* Text fields */}

      {endDate && startDate && endDate < startDate && (
        <p style={{ color: 'red' }}>
          לא ניתן להגדיר תאריך סיום שקטן מתאריך ההתחלה
        </p>
      )}

      <div>
        <FormControlLabel
          control={
            <Checkbox
              defaultChecked={data.timer || false}
              color="primary"
              onChange={(ev) => {
                setTimer(ev.currentTarget.checked);
                setDirty(true);
              }}
            />
          }
          label="אני רוצה להציג קאונטר (יש לוודא שהוזן תאריך סיום)"
        />
      </div>

      <div>
        <FormControlLabel
          style={{ marginBottom: '10px' }}
          control={
            <Checkbox
              defaultChecked={data.isLink}
              checked={isLinkChecked}
              onChange={(ev) => {
                const { checked } = ev.currentTarget;

                setIsLinkChecked(checked);
                setDirty(true);

                if (checked) setUrl('used-cars');
                else if (url === 'used-cars') setUrl('');
              }}
              name="isLink"
              color="primary"
            />
          }
          label="לינק חיפוש רכבים"
        />
      </div>

      <TextField
        required
        label="שם קמפיין"
        onBlur={(e) => setErrors({ campaignName: !e.target.value })}
        error={errors.campaignName}
        defaultValue={data.campaignName || ''}
        onChange={(e) => {
          setCampaignName(e.target.value);
          setDirty(true);
        }}
        fullWidth
        variant="outlined"
        style={{ marginBottom: 16 }}
        helperText={errors.campaignName && 'שדה זה חובה'}
      />

      <TextField
        required
        error={errors.url}
        helperText={errors.url && 'שדה זה חובה'}
        label="לינק לבחירת רכבים"
        value={url}
        onChange={(e) => {
          setUrl(e.target.value);
          setDirty(true);
          setCarsFound(-1);

          if (e.target.value.startsWith('used-cars')) {
            setIsLinkChecked(true);
          }
        }}
        onBlur={async (e) => {
          setIsLinkChecked(e.target.value.startsWith('used-cars'));

          await new Promise((resolve) => setTimeout(resolve, 0)); // wait until isCheckedLink is updated

          setErrors({ url: isLinkChecked && e.target && !e.target.value });
          setLinkError(
            isLinkChecked &&
              e.target &&
              e.target.value &&
              !e.target.value.startsWith('used-cars')
          );
        }}
        fullWidth
        variant="outlined"
        style={{ marginBottom: 16 }}
        InputProps={{
          style: { direction: 'ltr' },
          startAdornment: <LinkIcon />
        }}
      />

      {linkError && (
        <p style={{ color: 'red' }}>הלינק חייב להתחיל ב- “used-cars”</p>
      )}

      {/* Select input */}
      <FormControl fullWidth style={{ marginBottom: 16 }}>
        <Autocomplete
          multiple
          autoComplete
          disabled={!isLinkChecked}
          defaultValue={initialBranches}
          options={branchOpts}
          limitTags={3}
          style={{ width: '100%' }}
          disableCloseOnSelect
          inputValue={branchesInputValue}
          onInputChange={(event, value, reason) => {
            if (event && event.type === 'blur') {
              setBranchesInputValue('');
            } else if (reason !== 'reset') {
              setBranchesInputValue(value);
            }
          }}
          value={selectedBranches.map((id) =>
            branchOpts.find((option) => option.id === id)
          )}
          getOptionDisabled={(option) =>
            selectedBranches.includes(0) && option.id !== 0
          }
          onChange={(event, selectedOptions) => {
            const selectedIds = selectedOptions.map((option) => option.id);

            if (selectedIds.includes(0)) {
              // If "All Branches" is selected, clear other selections
              setSelectedBranches([0]);
            } else {
              setSelectedBranches(selectedIds);
            }

            setCarsFound(-1);
            setDirty(true);
          }}
          getOptionSelected={(option, value) => {
            return value && option.id === value.id;
          }}
          getOptionLabel={(option) => {
            if (!option) return null;

            return option.id !== 0
              ? `${option.agencyName} - ${option.areaName}`
              : 'כל הסניפים';
          }}
          renderOption={(option, { selected }) => (
            <>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
                disabled={option.id === 0}
              />
              {option?.id !== 0
                ? `${option.agencyName} - ${option.areaName}`
                : option.name}
            </>
          )}
          renderInput={(params) => (
            <TextField label="סניף" {...params} variant="outlined" />
          )}
        />
      </FormControl>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Button
          variant="contained"
          color="primary"
          helperText="* לא ניתן לשמור טרם ביצוע בדיקת רכבים"
          onClick={validateSearchUrl}
          disabled={
            loading || !url || !url.startsWith('used-cars') || !isLinkChecked
          }
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            'הצג כמות רכבים המתאימים לסינון'
          )}
        </Button>
        {carsFound !== null && carsFound >= 0 && (
          <Typography style={{ marginRight: 16 }}>
            {carsFound >= 10
              ? `נמצאו ${carsFound} רכבים `
              : `נמצאו ${carsFound} רכבים  , לא ניתן לסנן עבור מספר נמוך של רכבים`}
          </Typography>
        )}
      </div>
      {carsFound === -1 && isLinkChecked && (
        <Typography variant="caption" style={{ margin: 5 }}>
          * לא ניתן לשמור טרם ביצוע בדיקת רכבים
        </Typography>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 16,
          marginTop: 10
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={createPreviewLink}
          disabled={
            loading || !url || !url.startsWith('used-cars') || !isLinkChecked
          }
        >
          הכן URL מאוחד
        </Button>
      </div>

      {url && fullSearchUrl && <CopyLinkInput link={fullSearchUrl} />}

      <ImageKitFileUpload
        maxSizeMB={10}
        helpText={helpText}
        onUploadSuccess={setDesktopImage}
        onImageChange={(imageUrl) => {
          setDirty(true);
          setErrors({ desktopImage: !imageUrl });
        }}
        text="לחץ כאן כדי להעלות מדיה לדקסטופ"
        folder="hero-content"
        initialImage={desktopImage}
        required={errors.desktopImage}
        icon={<DesktopMacIcon fontSize="large" />}
      />

      <br />
      <ImageKitFileUpload
        maxSizeMB={10}
        helpText={helpText}
        onUploadSuccess={setMobileImage}
        onImageChange={(imageUrl) => {
          setDirty(true);
          setErrors({ mobileImage: !imageUrl });
        }}
        text="לחץ כאן כדי להעלות מדיה למובייל"
        folder="hero-content"
        initialImage={mobileImage}
        required={errors.mobileImage}
        icon={<PhoneAndroidIcon fontSize="large" />}
      />

      {/* Save and Cancel buttons */}
      <div style={{ textAlign: 'center', marginTop: '100px' }}>
        <Button
          disabled={!hasSomeData()}
          variant="outlined"
          onClick={() => setDialogOpen(true)}
          style={{
            marginLeft: 16,
            width: '40%',
            color: hasSomeData() ? 'red' : 'rgba(0, 0, 0, 0.26)',
            backgroundColor: hasSomeData() ? '' : 'rgba(0, 0, 0, 0.12)',
            border: hasSomeData() ? '1px solid red' : '1px solid lightgray'
          }}
        >
          הסרה
        </Button>

        <Button
          disabled={
            !dirty ||
            !areRequiredFieldsFilled() ||
            (carsFound !== null && carsFound < 10 && isLinkChecked)
          }
          variant="contained"
          color="primary"
          onClick={() => handleSave(false)}
          style={{ width: '40%' }}
        >
          שמירה
        </Button>
      </div>

      <RemoveDialog
        onConfirm={() => handleSave(true)}
        open={isDialogOpen}
        onCancel={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default BannerDrawer;
